"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { reportError } from "@/lib/error-reporting/report-error";
import {
  hasRecentlyReloaded,
  isChunkLoadError,
  markReloaded,
} from "@/lib/error-reporting/chunk-reload";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // ChunkLoadError かつ直近に自動再読み込みしていなければ、エラー画面を出さずに
  // 全体再読み込みして新版を取り直す。判定はマウント時に1回だけ行う。
  const [willReload] = useState(
    () =>
      typeof window !== "undefined" &&
      isChunkLoadError(error) &&
      !hasRecentlyReloaded(Date.now()),
  );

  useEffect(() => {
    if (willReload) {
      // 一時的な ChunkLoadError は報告しない（報告すると重要なエラーが
      // デプロイ直後のノイズに埋もれるため）。記録を残して再読み込みする。
      markReloaded(Date.now());
      window.location.reload();
      return;
    }
    // 再読み込みしても復旧しない ChunkLoadError、および ChunkLoadError 以外の
    // エラーはサーバーに報告する。
    reportError({
      error,
      source: "react_error_boundary",
      digest: error.digest,
    });
  }, [error, willReload]);

  if (willReload) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
        <p className="text-sm text-subtext">読み込み直しています…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center space-y-4">
        <Link href="/" className="inline-flex items-center justify-center mb-2">
          <img src="/logo_menren.svg" alt="MENREN" className="h-10" />
        </Link>
        <h1 className="text-xl font-semibold text-text">
          エラーが発生しました
        </h1>
        <p className="text-sm text-subtext leading-relaxed">
          一時的な問題が発生しました。
          <br />
          しばらくしてからもう一度お試しください。
        </p>
        <div className="flex flex-col gap-3 mt-2">
          <button
            onClick={reset}
            className="w-full bg-accent hover:bg-accent-hover text-on-accent px-6 py-3 rounded-lg text-sm font-medium transition-colors"
          >
            もう一度試す
          </button>
          <Link
            href="/"
            className="text-sm text-accent hover:text-accent-hover font-medium"
          >
            ホームに戻る
          </Link>
        </div>
      </div>
    </div>
  );
}
